import React from "react";
import { useEffect, useState } from "react";
import { BuilderComponent, builder, useIsPreviewing } from "@builder.io/react";
import { AppDispatch, RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import Error404Page from "../../pages/errors/404";
import SeoIgnore from "../../utils/seoIgnore";
import '@builder.io/widgets';
import dataService from "../../services/dataService";
import { BranchPageInfo, OverallBranches, StateBranches } from "../../models/builderIO/types";
import PageLoading from "../../components/shared/pageLoading";
import { fetchWarehouseDetails } from '../../store/slices/warehouseSlice'; 

interface BuilderIOPageProps {
  data?: any; //should contain the data for the page
  context?: any; //should contain actions (functions) that can be called in the page
  url?: string,
  onStateChange?: (state: any) => void;
}

const BuilderIOPage: React.FC<BuilderIOPageProps> = ({ data = {}, context, url, onStateChange }) => {
  const isPreviewingInBuilder = useIsPreviewing();
  const [notFound, setNotFound] = useState(false);
  const [content, setContent] = useState(null);
  const [branchPageInfo, setBranchPageInfo] = useState<BranchPageInfo | null>(null);
  const [stateBranches, setStateBranches] = useState<StateBranches | null>(null);
  const [overallBranches, setOverallBranches] = useState<OverallBranches | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const isAuthenticatedFromStore = useSelector((state: RootState) => state.userState.isAuthenticated);
  const userId = useSelector((state: RootState) => state.userState.user.id);
  const [isAuthenticated, setIsAuthenticated] = React.useState(isAuthenticatedFromStore);

  const [isLoading, setIsLoading] = useState(false);

  const stateChangedHandler = (state: any) => {
    setIsAuthenticated(state.isAuthenticated);
    console.log(state);
    if (onStateChange) {
      onStateChange(state);
    }
  };

  const selectedWarehouse = useSelector((state: RootState) => state.warehouseState?.selectedWarehouse?.warehouseCode);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      const clientUtcOffsetInMinutes = new Date().getTimezoneOffset();

      // Parse URL path segments
      const pathSegments = window.location.pathname.split('/').filter(segment => segment);
      const hasLansingBranches = pathSegments.includes('lansing-branches');
      const lansingSectionIndex = pathSegments.indexOf('lansing-branches');
      
      // Check if this is a state-level page, branch-specific page, or overall branches page
      const isOverallPage = hasLansingBranches && pathSegments.length === lansingSectionIndex + 1;
      const isStatePage = hasLansingBranches && pathSegments.length === lansingSectionIndex + 2;
      const isBranchPage = hasLansingBranches && pathSegments.length === lansingSectionIndex + 3;
      
      const stateFullName = isStatePage ? pathSegments[pathSegments.length - 1] : null;
      const warehouseDisplayName = isBranchPage ? pathSegments[pathSegments.length - 1] : null;
      const stateNameForBranch = isBranchPage ? pathSegments[pathSegments.length - 2] : null;


      if (warehouseDisplayName && stateNameForBranch) {

        const branchDetails = await dataService.getBranchDetails(
          warehouseDisplayName.replace("-bu", "").replace("-medenica", "").replace("-main", "").replace(/-test.*$/, ""),//fix for scott testing
          stateNameForBranch.replace("-bu", "").replace("-medenica", "").replace("-main", "").replace(/-test.*$/, ""),//fix for scott testing
          isAuthenticated,
          clientUtcOffsetInMinutes,
          userId
        );

        setBranchPageInfo(branchDetails.data);
      }

      if (stateFullName) {
        const stateBranches = await dataService.getStateBranches(stateFullName.replace(/-test.*$/, "").replace("-bu", "").replace("-medenica", "").replace("-main", ""), clientUtcOffsetInMinutes);
        setStateBranches(stateBranches.data);
      }

      if (isOverallPage) {
        const overallBranches = await dataService.getOverallBranches();
        setOverallBranches(overallBranches.data);
      }

      const content = await builder
        .get("page", {
          url: url ?? window.location.pathname
            .replace("/" + process.env.REACT_APP_SUBDIRECTORY, "")
            .replace("/" + process.env.REACT_APP_STOREFRONT_SUBDIRECTORY, "")
        })
        .promise();

      setContent(content);
      setNotFound(!content);

      // if the page title is found,
      // set the document title
      if (content?.data.title) {
        document.title = content.data.title;
      }

      setIsLoading(false);
    }

    fetchData();
  }, [isAuthenticated, url]);

  const reactWebUrl = isAuthenticated 
    ? process.env.REACT_APP_WEB_URL + "/" + process.env.REACT_APP_STOREFRONT_SUBDIRECTORY
    : process.env.REACT_APP_WEB_URL + "/" + process.env.REACT_APP_SUBDIRECTORY;

  if (notFound && !isPreviewingInBuilder) {
    return <Error404Page />;
  }

  if (isLoading) {
    return <PageLoading />
  }

  // return the page when foundhjjh   
  return (
    <div className={isAuthenticated ? "builder-page-width builder-page-width-logged" : "builder-page-width"}>
      <SeoIgnore></SeoIgnore>
      {/* Render the Builder page */}
      <BuilderComponent
        model="page"
        content={content!}
        onStateChange={stateChangedHandler}
        data={{
          ...data,
          isAuthenticated: isAuthenticated,
          ...(branchPageInfo && { branchPageDetails: branchPageInfo }),
          ...(branchPageInfo && { categories: branchPageInfo.categories }),
          ...(stateBranches && { statePageDetails: stateBranches }),
          ...(overallBranches && { overallPageDetails: overallBranches }),
          apiUrl: process.env.REACT_APP_STOREFRONT_API_URL?.replace("https://", ""),
          webUrl: process.env.REACT_APP_WEB_URL?.replace("https://", ""),
          reactWebUrl: reactWebUrl,
          baseWebUrl: process.env.REACT_APP_WEB_URL,
          selectedWarehouse: selectedWarehouse 
        }}
        context={
          context
            ? {
                ...context,
                setSelectedWarehouse: async(newWarehouseCode: string) => {
                  await dispatch(fetchWarehouseDetails(newWarehouseCode));
                },
              }
            : {
                setSelectedWarehouse: async (newWarehouseCode: string) => {
                  await dispatch(fetchWarehouseDetails(newWarehouseCode));
                },
              }
        }
      />
    </div>
  );
};

export default BuilderIOPage;
